/**
 * Working days for leave: optionally per-employee (working days + day schedule), excluding public holidays.
 * Matches backend holidayRoutes isWorkingDayForEmployee / countWorkingDaysInRangeForEmployee.
 */

export const DEFAULT_WORKING_DAYS = [1, 2, 3, 4, 5]; // Mon=1 … Fri=5 (Date.getDay())

/** YYYY-MM-DD for a Date (local date parts). */
export function toDateKey(d) {
  const x = new Date(d);
  const y = x.getFullYear();
  const m = String(x.getMonth() + 1).padStart(2, '0');
  const day = String(x.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * @typedef {{ workingDays?: number[], daySchedule?: Record<string, string> }} EmployeeWorkingSettings
 */

function normalizeWorkingDays(raw) {
  if (!Array.isArray(raw) || raw.length === 0) return DEFAULT_WORKING_DAYS;
  const unique = [...new Set(raw.map((d) => Number(d)).filter((d) => d >= 1 && d <= 5))].sort((a, b) => a - b);
  return unique.length ? unique : DEFAULT_WORKING_DAYS;
}

function getActiveOverrideRule(dateKey, overrideWindows = null, adminId = null) {
  if (!Array.isArray(overrideWindows) || overrideWindows.length === 0) return null;
  const day = new Date(`${dateKey}T12:00:00`);
  const dayMs = day.getTime();
  const id = adminId != null ? String(adminId) : null;
  const candidates = overrideWindows.filter((w) => {
    if (!w) return false;
    if (id && w.adminId != null && String(w.adminId) !== id) return false;
    const start = new Date(w.startDate || w.start);
    const end = new Date(w.endDate || w.end);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return false;
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
    return dayMs >= start.getTime() && dayMs <= end.getTime();
  });
  if (candidates.length === 0) return null;
  candidates.sort((a, b) => new Date(b.updatedAt || b.createdAt || 0).getTime() - new Date(a.updatedAt || a.createdAt || 0).getTime());
  return candidates[0];
}

/**
 * True if this calendar day is a working day for the employee (same rules as backend).
 * @param {string} dateKey - YYYY-MM-DD
 * @param {EmployeeWorkingSettings | null | undefined} settings - null/undefined → Mon–Fri
 */
export function isWorkingDayForEmployee(dateKey, settings, overrideWindows = null, adminId = null) {
  const d = new Date(`${dateKey}T12:00:00`);
  const weekday = d.getDay();
  if (weekday === 0 || weekday === 6) return false;
  const activeOverride = getActiveOverrideRule(dateKey, overrideWindows, adminId);
  if (activeOverride) {
    const overrideDays = normalizeWorkingDays(activeOverride.workingDays);
    return overrideDays.includes(weekday);
  }
  if (settings == null) return true;
  const daySchedule = settings.daySchedule;
  if (daySchedule && typeof daySchedule === 'object' && daySchedule[String(weekday)] === 'off') return false;
  const workingDays = normalizeWorkingDays(settings.workingDays);
  return workingDays.includes(weekday);
}

/** Leave-day weight for a calendar day: 0, 0.5 (partial contract shift), or 1. */
export function getWorkingDayFraction(dateKey, settings, overrideWindows = null, adminId = null) {
  const d = new Date(`${dateKey}T12:00:00`);
  const weekday = d.getDay();
  if (weekday === 0 || weekday === 6) return 0;
  const activeOverride = getActiveOverrideRule(dateKey, overrideWindows, adminId);
  if (activeOverride) {
    const overrideDays = normalizeWorkingDays(activeOverride.workingDays);
    return overrideDays.includes(weekday) ? 1 : 0;
  }
  if (settings == null) return 1;
  const ds = settings.daySchedule;
  const v = ds && typeof ds === 'object' ? ds[String(weekday)] : undefined;
  if (v === 'off') return 0;
  if (v === 'morning' || v === 'afternoon') return 0.5;
  const workingDays = normalizeWorkingDays(settings.workingDays);
  return workingDays.includes(weekday) ? 1 : 0;
}

/**
 * Build a Set of 'YYYY-MM-DD' from an array of public holiday objects.
 * Supports multi-day: { date, endDate } adds every day in [date, endDate] inclusive.
 * @param {Array<{ date: string | Date, endDate?: string | Date } | string>} publicHolidays
 * @returns {Set<string>}
 */
export function publicHolidayDateSet(publicHolidays) {
  const set = new Set();
  if (!Array.isArray(publicHolidays)) return set;
  publicHolidays.forEach((h) => {
    if (typeof h !== 'object' || h == null || h.date == null) return;
    const start = new Date(h.date);
    start.setHours(0, 0, 0, 0);
    const end = h.endDate ? new Date(h.endDate) : new Date(h.date);
    end.setHours(0, 0, 0, 0);
    const d = new Date(start);
    while (d <= end) {
      set.add(toDateKey(d));
      d.setDate(d.getDate() + 1);
    }
  });
  return set;
}

/**
 * Count days in [start, end] that count toward leave: employee working days, not public holidays.
 * @param {string | Date} start
 * @param {string | Date} end
 * @param {Set<string>} publicHolidayDateSet
 * @param {EmployeeWorkingSettings | null} [employeeWorkingSettings] - omit/null → Mon–Fri weekdays only (legacy)
 * @returns {number}
 */
export function countWorkingDaysInRange(start, end, publicHolidayDateSet, employeeWorkingSettings = null, overrideWindows = null, adminId = null) {
  const startD = new Date(start);
  startD.setHours(0, 0, 0, 0);
  const endD = new Date(end);
  endD.setHours(0, 0, 0, 0);
  let count = 0;
  const d = new Date(startD);
  while (d <= endD) {
    const key = toDateKey(d);
    if (publicHolidayDateSet.has(key)) {
      d.setDate(d.getDate() + 1);
      continue;
    }
    if (employeeWorkingSettings == null) {
      const weekday = d.getDay();
      if (weekday >= 1 && weekday <= 5) count += 1;
    } else {
      count += getWorkingDayFraction(key, employeeWorkingSettings, overrideWindows, adminId);
    }
    d.setDate(d.getDate() + 1);
  }
  return count;
}

/**
 * Leave days for a request. A single half day is 0.5. A longer range with half day(s)
 * counts each requested half day as 0.5 (1.5, 2.5, and so on).
 */
export function countRequestedLeaveDays(
  start,
  end,
  holidaySet,
  employeeWorkingSettings = null,
  overrideWindows = null,
  adminId = null,
  halfDay = null,
  halfDayOn = null,
  halfDays = null
) {
  const startD = new Date(start);
  startD.setHours(0, 0, 0, 0);
  const endD = new Date(end);
  endD.setHours(0, 0, 0, 0);
  if (endD < startD) return 0;
  const startKey = toDateKey(startD);
  const endKey = toDateKey(endD);
  const sameDay = startKey === endKey;
  const halfMap = (halfDays && typeof halfDays === 'object' && !Array.isArray(halfDays)) ? { ...halfDays } : {};
  if ((halfDay === 'am' || halfDay === 'pm') && sameDay) return 0.5;
  if (sameDay && (halfMap[startKey] === 'am' || halfMap[startKey] === 'pm')) return 0.5;
  let total = countWorkingDaysInRange(startD, endD, holidaySet, employeeWorkingSettings, overrideWindows, adminId);
  if ((halfDay === 'am' || halfDay === 'pm') && (halfDayOn === 'start' || halfDayOn === 'end')) {
    const edgeKey = halfDayOn === 'start' ? startKey : endKey;
    if (!halfMap[edgeKey]) halfMap[edgeKey] = halfDay;
  }
  const halfKeys = Object.keys(halfMap).filter((k) => halfMap[k] === 'am' || halfMap[k] === 'pm');
  for (const key of halfKeys) {
    const d = new Date(`${key}T12:00:00`);
    if (Number.isNaN(d.getTime())) continue;
    const dStart = new Date(d);
    dStart.setHours(0, 0, 0, 0);
    if (dStart < startD || dStart > endD) continue;
    if (!holidaySet || !holidaySet.has(key)) {
      const fraction = employeeWorkingSettings == null
        ? (d.getDay() >= 1 && d.getDay() <= 5 ? 1 : 0)
        : getWorkingDayFraction(key, employeeWorkingSettings, overrideWindows, adminId);
      if (fraction >= 1) total = Math.round((total - 0.5) * 100) / 100;
    }
  }
  return total;
}

/**
 * @param {string | Date} start
 * @param {string | Date} end
 * @param {Set<string>} publicHolidayDateSet
 * @param {Map<string, string>} [publicHolidayNameMap]
 * @param {EmployeeWorkingSettings | null} [employeeWorkingSettings]
 * @returns {{ date: Date, dateKey: string, type: 'counted'|'half'|'weekend'|'public_holiday'|'non_working', label?: string }[]}
 */
export function getDaysBreakdown(start, end, publicHolidayDateSet, publicHolidayNameMap = null, employeeWorkingSettings = null, overrideWindows = null, adminId = null) {
  const startD = new Date(start);
  startD.setHours(0, 0, 0, 0);
  const endD = new Date(end);
  endD.setHours(0, 0, 0, 0);
  const result = [];
  const d = new Date(startD);
  while (d <= endD) {
    const dateKey = toDateKey(d);
    const weekday = d.getDay();
    const isWeekend = weekday === 0 || weekday === 6;
    const isPublicHoliday = publicHolidayDateSet && publicHolidayDateSet.has(dateKey);
    let type = 'counted';
    let label;
    if (isPublicHoliday) {
      type = 'public_holiday';
      label = (publicHolidayNameMap && publicHolidayNameMap.get(dateKey)) || 'Public holiday';
    } else if (isWeekend) {
      type = 'weekend';
      label = weekday === 0 ? 'Sunday' : 'Saturday';
    } else if (employeeWorkingSettings != null && !isWorkingDayForEmployee(dateKey, employeeWorkingSettings, overrideWindows, adminId)) {
      type = 'non_working';
      label = 'Non-working day (your schedule)';
    } else if (employeeWorkingSettings != null && getWorkingDayFraction(dateKey, employeeWorkingSettings, overrideWindows, adminId) === 0.5) {
      type = 'half';
      label = 'Half day (your schedule)';
    }
    result.push({ date: new Date(d), dateKey, type, label });
    d.setDate(d.getDate() + 1);
  }
  return result;
}
